const DEFAULT_API_BASE = process.env.VUE_APP_MYPORTAL_API_BASE_URL || 'https://myportal.de'

const IMAGE_PRESETS = {
  thumb: { width: 480, height: 360, quality: 75 },
  gallery: { width: 1280, height: 960, quality: 82 },
  // Volle Viewport-Breite + Parallax-Scroll — nicht unter ~1920px Breite gehen
  parallax: { width: 2560, height: 1080, quality: 88 },
  background: { width: 1280, height: 720, quality: 78 }
}

export function getImagePreset(name) {
  return IMAGE_PRESETS[name] || IMAGE_PRESETS.thumb
}

const MYPORTAL_PROCESS_PATH = '/api/v2/images/'

const MYPORTAL_IMAGE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** MongoDB-Dokument-IDs (24 Hex) sind keine MyPortal-Bild-IDs. */
const MONGODB_OBJECT_ID = /^[a-f0-9]{24}$/i

/** Nicht-leerer Anzeigetext (trim), z. B. für optionale CMS-Felder. */
export function hasDisplayText(value) {
  if (value == null) return false
  return String(value).trim().length > 0
}

export function isLegacyImageUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value)
}

export function isMyPortalImageId(value) {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed || isLegacyImageUrl(trimmed)) return false
  if (MONGODB_OBJECT_ID.test(trimmed)) return false
  return MYPORTAL_IMAGE_ID.test(trimmed)
}

/**
 * Liest die MyPortal-Bild-ID aus CMS-Datensätzen (gallery, general_images, …).
 * Nutzt bewusst nie `_id` — das ist die Dokument-ID, nicht die Bilddatei.
 */
export function extractImageId(record) {
  if (!record || typeof record !== 'object') return ''

  const candidates = [
    record.image_url,
    record.image,
    record.main_image,
    record.imageId
  ]

  for (const value of candidates) {
    if (isMyPortalImageId(value)) return value.trim()
    const fromProcessUrl = parseProcessUrlImageId(value)
    if (fromProcessUrl) return fromProcessUrl
  }

  return ''
}

export function parseProcessUrlImageId(source) {
  if (typeof source !== 'string') return ''

  const match = source.match(/\/api\/v2\/images\/([^/?#]+)\/process/i)
  const imageId = match?.[1]?.trim()

  return isMyPortalImageId(imageId) ? imageId : ''
}

export function appendCacheBust(url, token = Date.now()) {
  if (!url) return ''
  return `${url}${url.includes('?') ? '&' : '?'}_cb=${token}`
}

export function resolveLegacyImageUrl(record) {
  if (!record || typeof record !== 'object') return ''

  const candidates = [record.external_url, record.image_url, record.image].filter(Boolean)

  for (const value of candidates) {
    if (!isLegacyImageUrl(value)) continue
    if (value.includes('/process')) {
      const imageId = parseProcessUrlImageId(value)
      if (imageId) return getMyPortalImageProcessUrl(imageId, 'thumb')
      continue
    }
    return value
  }

  return ''
}

function apiBaseUrl() {
  return DEFAULT_API_BASE.replace(/\/$/, '')
}

export function isMyPortalProcessUrl(source) {
  return (
    typeof source === 'string' &&
    source.includes(MYPORTAL_PROCESS_PATH) &&
    source.includes('/process?')
  )
}

/** MyPortal /process antwortet bei zu vielen parallelen GETs mit 404 (Rate-Limit). */
const PROCESS_LOAD_MAX_CONCURRENT = 2
let processLoadActive = 0
const processLoadWaitQueue = []
const processBlobCache = new Map()
const processLoadInflight = new Map()

function runWithProcessLoadLimit(task) {
  return new Promise((resolve, reject) => {
    const execute = () => {
      processLoadActive += 1
      Promise.resolve()
        .then(task)
        .then(resolve, reject)
        .finally(() => {
          processLoadActive -= 1
          const next = processLoadWaitQueue.shift()
          if (next) next()
        })
    }

    if (processLoadActive < PROCESS_LOAD_MAX_CONCURRENT) {
      execute()
    } else {
      processLoadWaitQueue.push(execute)
    }
  })
}

async function fetchProcessImageBlob(url) {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Process image HTTP ${response.status}`)
  }
  const blob = await response.blob()
  if (!blob.size) {
    throw new Error('Process image empty')
  }
  return URL.createObjectURL(blob)
}

/**
 * Lädt Process-URLs seriell (max. 2 parallel), cached als Blob-URL — ein Request pro Bild.
 */
export function loadProcessImageUrl(url, { retries = 3, delayMs = 450 } = {}) {
  if (!url || !isMyPortalProcessUrl(url)) {
    return Promise.resolve(isLegacyImageUrl(url) ? url : '')
  }

  if (processBlobCache.has(url)) {
    return Promise.resolve(processBlobCache.get(url))
  }

  if (processLoadInflight.has(url)) {
    return processLoadInflight.get(url)
  }

  const promise = (async () => {
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      const candidate = attempt === 0 ? url : appendCacheBust(url)
      try {
        const blobUrl = await runWithProcessLoadLimit(() => fetchProcessImageBlob(candidate))
        processBlobCache.set(url, blobUrl)
        return blobUrl
      } catch {
        if (attempt >= retries) return ''
        await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)))
      }
    }
    return ''
  })().finally(() => {
    processLoadInflight.delete(url)
  })

  processLoadInflight.set(url, promise)
  return promise
}

/** @deprecated Alias — bitte loadProcessImageUrl verwenden */
export function verifyProcessImageUrl(url, options = {}) {
  return loadProcessImageUrl(url, options)
}

function buildProcessParams({ width, height, quality, fit = 'cover' }) {
  const params = new URLSearchParams({
    operation: 'resize',
    width: String(width),
    fit,
    format: 'webp',
    quality: String(quality)
  })

  if (height !== undefined) {
    params.set('height', String(height))
  }

  return params
}

/**
 * Öffentliche Process-URL wie SommerkinoVue – Resize/WebP serverseitig, kein Blob/CORS.
 * @see SommerkinoVue/src/lib/myportal/images.ts buildImageProcessUrl
 */
export function getMyPortalImageProcessUrl(imageId, presetOrOptions = 'thumb') {
  if (!isMyPortalImageId(imageId)) return ''

  const preset =
    typeof presetOrOptions === 'string'
      ? IMAGE_PRESETS[presetOrOptions] || IMAGE_PRESETS.thumb
      : presetOrOptions

  const params = buildProcessParams(preset)
  return `${apiBaseUrl()}/api/v2/images/${encodeURIComponent(imageId)}/process?${params.toString()}`
}

/** Process-URL auf anderes Preset umbiegen (gleiche Bild-ID). */
export function retargetMyPortalImageUrl(source, presetOrOptions) {
  if (!isMyPortalProcessUrl(source)) return source

  const preset =
    typeof presetOrOptions === 'string'
      ? IMAGE_PRESETS[presetOrOptions] || IMAGE_PRESETS.thumb
      : presetOrOptions

  try {
    const url = new URL(source)
    const params = buildProcessParams(preset)
    url.search = params.toString()
    return url.toString()
  } catch {
    return source
  }
}

export function resolveImageField(imageId, externalUrl, preset = 'thumb') {
  const resolvedId =
    typeof imageId === 'object' && imageId !== null
      ? extractImageId(imageId)
      : isMyPortalImageId(imageId)
        ? imageId.trim()
        : ''

  if (resolvedId) {
    return getMyPortalImageProcessUrl(resolvedId, preset)
  }

  if (externalUrl && isLegacyImageUrl(externalUrl)) {
    return externalUrl
  }

  return ''
}

/** @deprecated Alias – bitte getMyPortalImageProcessUrl verwenden */
export function getMyPortalImageDownloadUrl(imageId, options = {}) {
  if (options.raw) {
    return `${apiBaseUrl()}/api/v2/images/download/${encodeURIComponent(imageId)}`
  }

  if (typeof options.width === 'number' || typeof options.height === 'number') {
    return getMyPortalImageProcessUrl(imageId, {
      width: options.width || IMAGE_PRESETS.thumb.width,
      height: options.height,
      quality: options.quality ?? IMAGE_PRESETS.thumb.quality,
      fit: options.fit || 'cover'
    })
  }

  return getMyPortalImageProcessUrl(imageId, 'thumb')
}

export const getOptimizedImage = async (_myportal, imageId, width = 800, height = 600, quality = 80) => {
  const resolvedId = typeof imageId === 'object' ? extractImageId(imageId) : imageId
  if (!isMyPortalImageId(resolvedId)) {
    console.warn('[Image] No valid image ID provided')
    return null
  }

  return getMyPortalImageProcessUrl(resolvedId, { width, height, quality, fit: 'cover' })
}

export const getOptimizedBackgroundImage = async (_myportal, background_image, preset = 'background') => {
  const imageId = extractImageId(background_image) || background_image?.image

  if (!imageId || !isMyPortalImageId(imageId)) {
    console.warn('[Background] No valid background image ID provided')
    return ''
  }

  const imageUrl = getMyPortalImageProcessUrl(imageId, preset)

  const displayUrl = await loadProcessImageUrl(imageUrl)
  if (!displayUrl) {
    console.warn('[Background] Process image unavailable:', imageId)
    return ''
  }

  return `background-image: url('${displayUrl}'); background-position: ${background_image.position || 'center center'}; ${
    background_image.right ? 'right: 0' : 'left: 0'
  }`
}
